import { useMemo, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, Select } from '@dayon/design-system';
import { RESIDENCE_UNKNOWN, listCountries } from '@dayon/contracts';

export interface FilterDraft {
  kyc: string;
  tier: string;
  agent: string;
  /** An ISO 3166-1 alpha-2 code, `UNKNOWN` for never asked, or '' for any. */
  residence: string;
  /** ANY · ONLY · EXCLUDED. '' is not a value — the default is the word ANY. */
  testAccount: string;
}

/**
 * The design's "More filters" dialog.
 *
 * FOUR of its fields are served by `/api/players` and apply for real; the other
 * three are disabled with the reason on each field rather than left out — an
 * operator who cannot see the control cannot tell whether the platform has the
 * filter or the screen forgot it.
 *
 * The fourth is Country of residence, which the design did not draw because the
 * column did not exist when it was drawn. It is a real filter with a real
 * predicate behind it, and it carries one option that is not a country — see
 * the field's own note.
 *
 * The draft is seeded when the dialog OPENS rather than held in the parent, so
 * cancelling really does discard. `openedWith` is the seed: comparing it to the
 * incoming value re-seeds exactly once per open, during render, so no frame
 * paints the previous session's draft.
 */
/*
 * ── A NOTICE THAT TOLD OPERATORS A BUILT FEATURE DID NOT EXIST ────────────
 *
 * Until 8 October 2026 the notice at the foot of this dialog read: "there is no
 * saved-view or filter-set table anywhere in identity or platform, so a view
 * cannot be stored."
 *
 * There is. `platform.admin_saved_view` landed in sql/720 and three routes have
 * served it since — list, create and delete — with a dialog and a column picker
 * built against them. Nobody updated this sentence, so this screen went on
 * telling operators that a feature they had been given did not exist, which is
 * the most expensive kind of wrong copy: it does not look broken, and the person
 * who reads it stops asking.
 *
 * `advice-names-a-real-control.test.ts` was written to catch exactly this shape
 * and did not cover this file. It does now.
 *
 * ── AND IT ROTTED A SECOND TIME, EXACTLY AS THIS COMMENT PREDICTED ─────────
 *
 * The 8 October replacement read "Saving a filter set as a named view is built,
 * and today it is on Transactions rather than here", and this comment ended
 * "wiring it onto this screen is a separate change, and when it lands this
 * sentence changes again."
 *
 * It landed the same week (`7fcb7f41`) and the sentence did not change. So for
 * a day /players carried a Saved views button beside More filters, mounted
 * `SavedViewsDialog` with `screen="PLAYERS"`, and told the operator standing on
 * it to go to another screen for the control four hundred lines above them.
 *
 * The lesson is not "remember next time". A sentence whose truth depends on
 * which screens a dialog is mounted on cannot be maintained by hand — so the
 * text no longer names a screen at all, and the widened test now refuses a
 * sentence that sends the reader elsewhere from a screen that has the control.
 */
export function FiltersDialog({
  open,
  onClose,
  current,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  current: FilterDraft;
  onApply: (next: FilterDraft) => void;
}) {
  /* Derived once: `listCountries` walks 676 code pairs through Intl.DisplayNames
     and the result cannot change while the dialog is mounted. */
  const countries = useMemo(() => listCountries(), []);
  const [draft, setDraft] = useState<FilterDraft>(current);
  const [openedWith, setOpenedWith] = useState(open);
  if (openedWith !== open) {
    setOpenedWith(open);
    if (open) setDraft(current);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="More filters"
      wide
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onApply({ ...draft, agent: draft.agent.trim() })}
          >
            Apply filters
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: '13px' }}>
        Filters combine with the status chips and the brand scope.
      </p>

      <div className="stack">
        <Field label="KYC state">
          {({ id }) => (
            <Select
              id={id}
              value={draft.kyc}
              onChange={(e) => setDraft((d) => ({ ...d, kyc: e.target.value }))}
            >
              <option value="">Any</option>
              <option value="VERIFIED">Verified</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
              <option value="UNVERIFIED">Unverified</option>
            </Select>
          )}
        </Field>

        <Field label="Tier">
          {({ id }) => (
            <Select
              id={id}
              value={draft.tier}
              onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value }))}
            >
              <option value="">Any</option>
              <option value="BRONZE">Bronze</option>
              <option value="SILVER">Silver</option>
              <option value="GOLD">Gold</option>
              <option value="VIP_3">VIP 3</option>
              <option value="VIP_4">VIP 4</option>
            </Select>
          )}
        </Field>

        {/* Matched exactly (`p.agent_ref = $1` in GET /api/players); the search box matches part
            of a code. */}
        <Field
          label="Agent"
          hint="The whole agent or master code, exactly as it is written — part of a code matches nothing here. The search box above finds part of a code."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={draft.agent}
              onChange={(e) => setDraft((d) => ({ ...d, agent: e.target.value }))}
              placeholder="AG-227"
            />
          )}
        </Field>

        <Field
          label="Country of residence"
          hint="Where the player said they live when they registered. The list is the same one the sign-up form offers, so a filter here can never name a country the platform would refuse to store."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.residence}
              onChange={(e) => setDraft((d) => ({ ...d, residence: e.target.value }))}
            >
              <option value="">Any</option>
              {/*
                NOT A COUNTRY, and deliberately first so it is not lost among
                two hundred that are.

                `residence_country` is nullable and every account that
                registered before the column existed carries NULL. That is the
                absence of the question, not an answer to it, and it is the
                population somebody has to work through if a licence rule is
                ever switched on — so it has to be selectable. It is a
                seven-letter sentinel because every ISO 3166-1 alpha-2 code is
                two letters and could not be confused with one.
              */}
              <option value={RESIDENCE_UNKNOWN}>Never asked — registered before the question</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/*
          TEST ACCOUNTS — PAGCOR item 33(c), and a REAL filter rather than one
          more disabled one.

          A marked account's play, deposits and withdrawals leave Gross Gaming
          Revenue, retroactively: the next nightly rebuild retracts its past
          days from months already reported. "Which accounts are excluded from
          the figures?" is the first question an inspection asks about that, and
          until this control the only way to answer it was to open profiles one
          at a time.

          Three states and not a checkbox. The default has to be "everything" —
          a roster that silently hid a population would make every count on this
          screen disagree with the database — and a checkbox cannot tell
          "everything" and "neither" apart.

          Placed here, with the four filters that WORK, rather than after the
          three that are drawn disabled. A working control filed among inert
          ones reads as inert.
        */}
        <Field
          label="Test accounts"
          hint="Accounts marked as test accounts are left out of the revenue figures, including for months already reported."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.testAccount}
              onChange={(e) => setDraft((d) => ({ ...d, testAccount: e.target.value }))}
            >
              <option value="ANY">Show them with everybody else</option>
              <option value="ONLY">Only test accounts</option>
              <option value="EXCLUDED">Hide test accounts</option>
            </Select>
          )}
        </Field>

        <Field
          label="Balance range (PHP)"
          hint="Not filterable. A balance is added up from the ledger for each player rather than held as a stored figure, so it cannot be used to narrow the roster. It IS sortable — the Sort control orders by that same figure."
        >
          {({ id, describedBy }) => (
            <div className="row">
              <Input id={id} aria-describedby={describedBy} disabled placeholder="Min" />
              <Input aria-label="Maximum balance" disabled placeholder="Max" />
            </div>
          )}
        </Field>

        <Field
          label="Registered"
          hint="Not filterable here. The registration date is kept and the roster can be sorted by it either way, and the roster Export takes a registration window — but the roster itself cannot be narrowed to a range of registration dates."
        >
          {({ id, describedBy }) => (
            <div className="row">
              <Input id={id} aria-describedby={describedBy} type="date" disabled />
              <Input aria-label="Registered to" type="date" disabled />
            </div>
          )}
        </Field>

        <Field
          label="Risk band"
          hint="Not filterable. The risk score is kept and comes back on every row, but the roster cannot be narrowed by it — and the Low / Medium / High banding is this console's own, not something the platform stores."
        >
          {({ id, describedBy }) => (
            <Select id={id} aria-describedby={describedBy} disabled defaultValue="">
              <option value="">Any</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </Select>
          )}
        </Field>

        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            Five of the eight filters apply for real, to the whole roster; the other three are
            disabled, each with its reason beside it. What you set here is written to the address
            bar — the filters, the sort and its direction — so the result is a link you can keep or
            send to somebody, and it reopens exactly as you left it.{' '}
            <em>Saved views</em>, beside More filters, keeps that link under a name you choose —
            so a filter set you rebuild every morning is a click rather than eight.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
